import { useSnapshot } from 'valtio';

import { frameRates } from '@cartwhl/common/types';
import { isNonProduction } from '@cartwhl/common/utils/getEnvironment';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Select from '@cartwhl/ui/atoms/Select';
import Slider from '@cartwhl/ui/atoms/Slider';
import ToggleGroup from '@cartwhl/ui/atoms/ToggleGroup';
import Text from '@cartwhl/ui/base/Text';
import BackgroundIcon from '@cartwhl/ui/icons/BackgroundIcon';
import GradientIcon from '@cartwhl/ui/icons/GradientIcon';
import Button from '@cartwhl/ui/input/Button';
import Panel from '@cartwhl/ui/layout/Panel';
import ColorPicker from '@cartwhl/ui/molecules/ColorPicker';
import PanelSection from '@cartwhl/ui/molecules/PanelSection';

import { sceneSettingsStore, uiStore } from 'stores';

const ASPECT_RATIOS = [
  {
    label: 'full',
    value: -1,
    content: 'Full',
  },
  {
    label: '16:9',
    value: 16 / 9,
    content: '16:9',
  },
  {
    label: '4:3',
    value: 4 / 3,
    content: '4:3',
  },
  {
    label: '1:1',
    value: 1,
    content: '1:1',
  },
  {
    label: '9:16',
    value: 9 / 16,
    content: '9:16',
  },
];

const gradientLabel = 'gradient';
const solidLabel = 'solid';
const BACKGROUND_OPTIONS = [
  {
    label: gradientLabel,
    value: gradientLabel,
    content: <GradientIcon />,
  },
  {
    label: solidLabel,
    value: solidLabel,
    content: <BackgroundIcon />,
  },
];

export default function SceneSettingsPanel({
  close,
  isOpen,
  enterTransitionDuration = 0,
  exitTransitionDuration = 0,
}: {
  close: () => void;
  isOpen?: boolean;
  enterTransitionDuration?: number;
  exitTransitionDuration?: number;
}) {
  const {
    backgroundSettings: {
      hexColor,
      enableShadows,
      showGrid,
      showGroundPlane,
      gridCellSize = 1,
      useBackgroundGradient,
    },
    cameraSettings: { aspectRatio, cameraFollow },
    playbackSettings: { frameRate, frameStepSize, playbackSpeed },
    characterSettings: { showSkeleton, enableRootMotion },
  } = useSnapshot(sceneSettingsStore.state);

  const { subPanelKey } = useSnapshot(uiStore.state);

  const hasPoseInPath = window.location.pathname.includes('pose');

  const isNonProductionEnvironment = isNonProduction();

  const handleGridCellSizeChange = (value: number) => {
    const clampedValue = Math.min(2, Math.max(0.25, value));
    sceneSettingsStore.set(
      'backgroundSettings',
      'gridCellSize',
      Number(clampedValue.toFixed(2)),
    );
  };

  return (
    <Panel
      title="Advanced"
      closePanel={close}
      subPanelKey={subPanelKey}
      isOpen={isOpen}
      enterTransitionDuration={enterTransitionDuration}
      exitTransitionDuration={exitTransitionDuration}
    >
      <div className="flex flex-col gap-6">
        {!hasPoseInPath && (
          <PanelSection
            title="Playback"
            content={[
              {
                title: 'Frame rate',
                children: (
                  <Select
                    value={frameRate}
                    onValueChange={(value) => {
                      sceneSettingsStore.set(
                        'playbackSettings',
                        'frameRate',
                        parseInt(value),
                      );
                    }}
                    items={frameRates.map((frameRate) => ({
                      label: frameRate.toString(),
                      value: frameRate,
                    }))}
                  />
                ),
              },
              {
                title: 'Frame hold size:',
                children: (
                  <div className="flex items-center gap-4">
                    <Slider
                      rangeColor="blue"
                      max={8}
                      min={1}
                      onChange={(value) => {
                        sceneSettingsStore.set(
                          'playbackSettings',
                          'frameStepSize',
                          value,
                        );
                      }}
                      currentValue={frameStepSize}
                    />
                    <Text
                      variant="studio/body/medium/xs"
                      className="bg-gray-15 dark:bg-gray-75 flex w-10 items-center justify-center rounded-lg py-[2px] tracking-tighter tabular-nums"
                    >
                      {frameStepSize}
                    </Text>
                  </div>
                ),
              },
              {
                title: 'Playback speed',
                children: (
                  <>
                    <div className="flex items-center gap-4">
                      <Slider
                        rangeColor="blue"
                        max={2}
                        min={0.1}
                        step={0.1}
                        onChange={(value) => {
                          sceneSettingsStore.set(
                            'playbackSettings',
                            'playbackSpeed',
                            value,
                          );
                        }}
                        currentValue={playbackSpeed}
                      />
                      <Text
                        variant="studio/body/medium/xs"
                        className="bg-gray-15 dark:bg-gray-75 flex h-6 w-12 items-center justify-center rounded-lg tracking-tighter tabular-nums"
                      >
                        {playbackSpeed}x
                      </Text>
                    </div>
                    {/* <Select
                    value={playbackSpeed}
                    onValueChange={(value) =>
                      // Ensures user can't deselect playback speed
                      value &&
                      sceneSettingsStore.set(
                        'playbackSettings',
                        'playbackSpeed',
                        parseFloat(value),
                      )
                    }
                    items={playbackSpeeds.map((ps) => ({
                      value: ps,
                      label: `${ps.toString().replace('0.', '.')}x`,
                    }))}
                  /> */}
                  </>
                ),
              },
            ]}
          />
        )}

        <PanelSection
          title="Scene"
          content={[
            {
              title: 'View options',
              children: (
                <div className="flex flex-col gap-4">
                  <div className="grid auto-rows-[34px] grid-cols-2 items-center gap-2">
                    <Checkbox
                      id="shadows-checkbox"
                      variant="checkbox"
                      label="Shadows"
                      color="settings"
                      checked={enableShadows || false}
                      setChecked={(checked) =>
                        sceneSettingsStore.set(
                          'backgroundSettings',
                          'enableShadows',
                          checked,
                        )
                      }
                    />
                    <Checkbox
                      id="mesh-checkbox"
                      variant="checkbox"
                      label="Mesh"
                      color="settings"
                      checked={!showSkeleton}
                      setChecked={(checked) =>
                        sceneSettingsStore.set(
                          'characterSettings',
                          'showSkeleton',
                          !checked,
                        )
                      }
                    />
                    <Checkbox
                      id="grid-checkbox"
                      variant="checkbox"
                      label="Grid"
                      color="settings"
                      checked={showGrid || false}
                      setChecked={(value) =>
                        sceneSettingsStore.set(
                          'backgroundSettings',
                          'showGrid',
                          value,
                        )
                      }
                    />
                    {isNonProductionEnvironment && (
                      <Checkbox
                        id="ground-plane-checkbox"
                        variant="checkbox"
                        label="Ground Plane"
                        color="settings"
                        checked={showGroundPlane || false}
                        setChecked={(value) =>
                          sceneSettingsStore.set(
                            'backgroundSettings',
                            'showGroundPlane',
                            value,
                          )
                        }
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="studio/body/sm" className="text-secondary">
                      Grid spacing
                    </Text>
                    <div className="flex items-center gap-4">
                      <Slider
                        rangeColor="blue"
                        min={0.25}
                        max={2}
                        step={0.05}
                        currentValue={gridCellSize}
                        onChange={handleGridCellSizeChange}
                      />
                      <Text
                        variant="studio/body/medium/xs"
                        className="bg-gray-15 dark:bg-gray-75 flex h-6 w-12 items-center justify-center rounded-lg tracking-tighter tabular-nums"
                      >
                        {gridCellSize.toFixed(2)}x
                      </Text>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              title: 'Background',
              children: (
                <div>
                  <ToggleGroup
                    className="mb-4"
                    items={BACKGROUND_OPTIONS}
                    value={
                      useBackgroundGradient === true
                        ? gradientLabel
                        : solidLabel
                    }
                    onValueChange={(value) =>
                      sceneSettingsStore.set(
                        'backgroundSettings',
                        'useBackgroundGradient',
                        value === gradientLabel,
                      )
                    }
                  />
                  <ColorPicker
                    value={hexColor}
                    onChange={(value) =>
                      sceneSettingsStore.set(
                        'backgroundSettings',
                        'hexColor',
                        value,
                      )
                    }
                  />
                </div>
              ),
            },
            {
              title: 'Aspect ratio',
              children: (
                <ToggleGroup
                  stretch
                  items={ASPECT_RATIOS}
                  onValueChange={(value) => {
                    sceneSettingsStore.set(
                      'cameraSettings',
                      'aspectRatio',
                      parseFloat(value),
                    );
                  }}
                  value={aspectRatio}
                />
              ),
            },
          ]}
        />

        {!hasPoseInPath && (
          <PanelSection
            title="Camera & Animation"
            content={[
              {
                children: (
                  <div className="grid auto-rows-[34px] grid-cols-2 items-center gap-2">
                    <Checkbox
                      id="root-motion-checkbox"
                      variant="checkbox"
                      label="Move in Place"
                      color="settings"
                      checked={!enableRootMotion}
                      setChecked={(checked) =>
                        sceneSettingsStore.set(
                          'characterSettings',
                          'enableRootMotion',
                          !checked,
                        )
                      }
                    />
                    <Checkbox
                      id="camera-follow-checkbox"
                      variant="checkbox"
                      label="Camera Follow"
                      color="settings"
                      checked={cameraFollow}
                      setChecked={(checked) => {
                        sceneSettingsStore.set(
                          'cameraSettings',
                          'cameraFollow',
                          checked,
                        );
                        sceneSettingsStore.set(
                          'backgroundSettings',
                          'showGrid',
                          checked,
                        );
                      }}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}

        <hr className="border-gray-30 dark:border-gray-75 my-6" />
        <div className="flex flex-row-reverse gap-2">
          <Button
            variant="secondary"
            onClick={() => sceneSettingsStore.reset()}
          >
            Reset all
          </Button>
        </div>
      </div>
    </Panel>
  );
}
